/**
 * Polish render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'pl')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const polishLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'rozmycie' },
    change: { primary: 'zmiana' },
    click: { primary: 'kliknięcie' },
    dblclick: { primary: 'podwójnekliknięcie' },
    focus: { primary: 'fokus' },
    input: { primary: 'wejście' },
    keydown: { primary: 'klawiszdół' },
    keypress: { primary: 'klawisznaciśnięcie' },
    keyup: { primary: 'klawiszgóra' },
    load: { primary: 'załaduj' },
    mousedown: { primary: 'mousedown' },
    mouseenter: { primary: 'myszwejście' },
    mouseleave: { primary: 'myszwyjście' },
    mousemove: { primary: 'myszruch' },
    mouseout: { primary: 'myszpoza' },
    mouseover: { primary: 'mysznad' },
    mouseup: { primary: 'mouseup' },
    reset: { primary: 'zresetuj' },
    resize: { primary: 'zmiana rozmiaru' },
    scroll: { primary: 'przewiń' },
    submit: { primary: 'wysłaniu' },
    touchcancel: { primary: 'dotykanuluj' },
    touchend: { primary: 'dotykkoniec' },
    touchmove: { primary: 'dotykruch' },
    touchstart: { primary: 'dotykstart' },
    unload: { primary: 'wyładuj' },
  },
  logical: {
    and: { primary: 'i' },
    changes: { primary: 'zmienia' },
    contains: { primary: 'zawiera' },
    else: { primary: 'inaczej' },
    end: { primary: 'koniec' },
    equals: { primary: 'równa się' },
    exists: { primary: 'istnieje' },
    has: { primary: 'ma' },
    have: { primary: 'mam' },
    includes: { primary: 'obejmuje' },
    is: { primary: 'jest' },
    live: { primary: 'żywy' },
    matches: { primary: 'pasuje' },
    not: { primary: 'nie' },
    or: { primary: 'lub' },
    otherwise: { primary: 'wpp' },
    then: { primary: 'wtedy' },
    when: { primary: 'kiedy' },
    where: { primary: 'gdzie' },
  },
  temporal: {
    forever: { primary: 'zawsze' },
    h: { primary: 'godz' },
    hour: { primary: 'godzina' },
    hours: { primary: 'godziny' },
    millisecond: { primary: 'milisekunda' },
    milliseconds: { primary: 'milisekundy' },
    min: { primary: 'min' },
    minute: { primary: 'minuta' },
    minutes: { primary: 'minuty' },
    ms: { primary: 'ms' },
    s: { primary: 's' },
    second: { primary: 'sekunda' },
    seconds: { primary: 'sekundy' },
    times: { primary: 'razy' },
  },
  values: {
    body: { primary: 'body' },
    detail: { primary: 'szczegół' },
    document: { primary: 'dokument' },
    element: { primary: 'element' },
    event: { primary: 'zdarzenie' },
    false: { primary: 'fałsz' },
    it: { primary: 'to' },
    its: { primary: 'jego' },
    me: { primary: 'ja' },
    my: { primary: 'mój' },
    myself: { primary: 'ja sam' },
    null: { primary: 'null' },
    result: { primary: 'wynik' },
    target: { primary: 'cel' },
    true: { primary: 'prawda' },
    undefined: { primary: 'niezdefiniowane' },
    value: { primary: 'wartość' },
    window: { primary: 'okno' },
    you: { primary: 'ty' },
    your: { primary: 'twój' },
    yourself: { primary: 'ty sam' },
  },
  attributes: {
    attribute: { primary: 'atrybut' },
    attributes: { primary: 'atrybuty' },
    class: { primary: 'klasa' },
    classes: { primary: 'klasy' },
    properties: { primary: 'właściwości' },
    property: { primary: 'właściwość' },
    style: { primary: 'styl' },
    styles: { primary: 'style' },
  },
  expressions: {
    up: { primary: 'w górę' },
    down: { primary: 'w dół' },
    left: { primary: 'w lewo' },
    right: { primary: 'w prawo' },
    at: { primary: 'przy' },
    back: { primary: 'wstecz' },
    characters: { primary: 'znaki' },
    children: { primary: 'dzieci' },
    closest: { primary: 'najbliższy' },
    elsewhere: { primary: 'zewnątrz' },
    empty: { primary: 'pusty' },
    'ends with': { primary: 'kończy się na' },
    exclusive: { primary: 'wyłącznie' },
    first: { primary: 'pierwszy' },
    'ignoring case': { primary: 'ignorując wielkość liter' },
    inclusive: { primary: 'włącznie' },
    index: { primary: 'indeks' },
    'joined by': { primary: 'połączone przez' },
    last: { primary: 'ostatni' },
    'mapped to': { primary: 'przekształcone na' },
    next: { primary: 'następny' },
    no: { primary: 'brak' },
    parent: { primary: 'rodzic' },
    prev: { primary: 'poprz' },
    previous: { primary: 'poprzedni' },
    random: { primary: 'losowy' },
    some: { primary: 'jakiś' },
    'sorted by': { primary: 'sortowane według' },
    'split by': { primary: 'podzielone przez' },
    start: { primary: 'rozpocznij' },
    catch: { primary: 'złap' },
    finally: { primary: 'ostatecznie' },
    def: { primary: 'funkcja' },
    'starts with': { primary: 'zaczyna się od' },
    within: { primary: 'wewnątrz' },
  },
};

registerLexicon('pl', polishLexicon);
